import type {
  CommissionLine,
  CommissionKind,
  CommissionStatus,
  DueRule,
  ProducerStatus,
  RecruiterStatus,
  ReviewStatus,
  Stage,
  StatementStatus,
} from "./types";

type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

export const money = (n: number | null | undefined): string =>
  `$${Math.round(Number(n) || 0).toLocaleString("es-CL")}`;

export const pct = (rate: number): string => `${(Math.round(rate * 10000) / 100).toLocaleString("es-CL")} %`;

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const WEEKDAYS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

/** "2026-10-14" → "mié 14 oct". */
export function day(date: string | null | undefined): string {
  if (!date) return "—";
  const [y, m, d] = date.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return date;
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${WEEKDAYS[wd]} ${d} ${MONTHS[m - 1]}`;
}

/** "2026-10-14" → "14 oct 2026". */
export function dayLong(date: string | null | undefined): string {
  if (!date) return "—";
  const [y, m, d] = date.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return date;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** Días corridos de a → b ("YYYY-MM-DD"). */
export function daysBetween(a: string, b: string): number {
  const ta = Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10));
  const tb = Date.UTC(+b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10));
  return Math.round((tb - ta) / 86400000);
}

/** "en 3 días", "hoy", "mañana", "hace 2 días". */
export function relative(date: string | null | undefined, today: string): string {
  if (!date) return "";
  const d = daysBetween(today, date);
  if (d === 0) return "hoy";
  if (d === 1) return "mañana";
  if (d === -1) return "ayer";
  return d > 0 ? `en ${d} días` : `hace ${-d} días`;
}

export const RECRUITER_STATUS: Record<RecruiterStatus, { label: string; tone: Tone }> = {
  active: { label: "Activo", tone: "success" },
  paused: { label: "En pausa", tone: "warning" },
  ended: { label: "Terminado", tone: "neutral" },
};

export const PRODUCER_STATUS: Record<ProducerStatus, { label: string; tone: Tone }> = {
  proposed: { label: "Propuesto", tone: "info" },
  pipeline: { label: "En pipeline", tone: "accent" },
  active: { label: "Activo", tone: "success" },
  expired: { label: "Caducado", tone: "neutral" },
  rejected: { label: "Rechazado", tone: "neutral" },
  released: { label: "Liberado", tone: "neutral" },
};

export const STAGE: Record<Stage, string> = {
  activation: "Activación",
  maintenance: "Mantención",
};

export const REVIEW: Record<ReviewStatus, { label: string; tone: Tone }> = {
  pending: { label: "Por revisar", tone: "info" },
  approved: { label: "Aprobado", tone: "success" },
  observed: { label: "Observado", tone: "warning" },
  retained: { label: "Retenido", tone: "danger" },
};

export const COMMISSION_STATUS: Record<CommissionStatus, { label: string; tone: Tone; help: string }> = {
  estimated: { label: "Estimada", tone: "neutral", help: "El evento aún no se concilia: el monto puede cambiar." },
  pending_checklist: { label: "Por checklist", tone: "info", help: "Conciliada: falta que Mixcore apruebe el checklist." },
  payable: { label: "Por pagar", tone: "accent", help: "Entra en la próxima liquidación mensual." },
  in_statement: { label: "En liquidación", tone: "warning", help: "Está en una liquidación: falta la boleta o el pago." },
  paid: { label: "Pagada", tone: "success", help: "Transferida." },
  retained: { label: "Retenida", tone: "danger", help: "Lo observado no se subsanó dentro del plazo." },
  void: { label: "Sin comisión", tone: "neutral", help: "El evento no generó comisión." },
};

export const COMMISSION_KIND: Record<CommissionKind, string> = {
  origination: "Fee de originación",
  event: "Evento",
  adjustment: "Ajuste",
};

export const STATEMENT_STATUS: Record<StatementStatus, { label: string; tone: Tone }> = {
  awaiting_invoice: { label: "Esperando boleta", tone: "warning" },
  invoice_submitted: { label: "Boleta recibida", tone: "info" },
  paid: { label: "Pagada", tone: "success" },
};

export const NA_REASON: Record<string, string> = {
  force_majeure: "Fuerza mayor",
  simultaneous: "Evento simultáneo",
  not_requested: "El productor no pidió presencia",
  event_canceled: "Evento cancelado",
};

export function dueLabel(rule: DueRule): string {
  switch (rule.kind) {
    case "before_event_hours":
      return `${rule.hours} h antes del evento`;
    case "event_day":
      return "El día del evento";
    case "after_event_business_days":
      return `${rule.days} días hábiles después`;
    default:
      return "Sin plazo";
  }
}

export const TONE_CLASS: Record<Tone, string> = {
  neutral: "nx-badge-neutral",
  accent: "nx-badge-accent",
  success: "nx-badge-success",
  warning: "nx-badge-warning",
  danger: "nx-badge-danger",
  info: "nx-badge-info",
};

/** Mes anterior al de `today` ("YYYY-MM"). */
export function previousPeriod(today: string): string {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
}

export function periodLabel(period: string): string {
  const full = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  const [y, m] = period.split("-");
  return `${full[Number(m) - 1] ?? m} ${y}`;
}

export function lineTitle(c: CommissionLine): string {
  if (c.kind === "event") return `${c.eventName ?? `Evento ${c.eventId}`} · ${STAGE[c.stage ?? "activation"]} n.º ${c.number ?? "—"}`;
  if (c.kind === "origination") return `${COMMISSION_KIND.origination}${c.eventName ? ` · ${c.eventName}` : ""}`;
  return `${COMMISSION_KIND.adjustment}: ${c.description ?? ""}`;
}

/** Fechas del programa (firma del acuerdo, atribución): desde 2025 y no futuras. */
export const PROGRAM_MIN_DATE = "2025-01-01";

/** Hoy en Chile, "YYYY-MM-DD". */
export function chileToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Santiago" }).format(new Date());
}
